/**
 * Match Element Player
 * Pure renderer -- two-column layout with drag-and-drop and click-to-select.
 * Grading, submit, retry, and feedback display are handled by ContentPlayer (content-core).
 */

import * as React from 'react';
import {
    DndContext,
    DragOverlay,
    useDraggable,
    useDroppable,
    useSensor,
    useSensors,
    PointerSensor,
    TouchSensor,
    type DragStartEvent,
    type DragEndEvent,
    DroppableZone,
    type DroppableState,
    DraggableItem,
    type DraggableState,
    cn,
    Badge,
    Button,
    shuffleArray,
} from '@ai-content-hub/content-kit';
import type { ElementPlayerProps, BehaviorConfig, GradingResult } from '@ai-content-hub/content-kit';
import { HelpCircle, X } from 'lucide-react';
import {
    type MatchElementData,
    type MatchAnswer,
    type MatchItem,
    type MatchOption,
    normalizeMatchData,
    getItemText,
    getOptionText,
    matchElementSchema,
} from './schema';
import { BlockRenderer, getDisplayBlockContent } from '../block';
import { useElementSchemaLocale } from '../i18n/useElementSchemaLocale';

export interface MatchPlayerProps extends ElementPlayerProps<MatchElementData, MatchAnswer> {
    /** Host-driven pulse (e.g. question set nav jump) for this row id. */
    highlightMatchItemId?: string | null;
}

const DROPPABLE_PREFIX = 'droppable-';
const DRAGGABLE_PREFIX = 'draggable-';
const RETURN_PREFIX = 'return-';
const OPTIONS_RETURN_ID = 'options-return-zone';

// Shared by item / option / drag-overlay cards. No overflow-hidden and no h-full on the
// renderer: the card grows to fit its content (text top-aligned, images capped at 200px)
// instead of clipping 12-40px of it. Text is left-aligned; figures stay centred.
const MATCH_CARD_CONTENT_CLASS =
    'flex flex-1 min-w-0 items-start justify-start [&_.tiptap-content]:bg-transparent [&_.block-renderer]:flex [&_.block-renderer]:flex-col [&_.block-renderer]:items-start [&_.block-renderer]:max-w-full [&_.block-renderer]:break-words [&_figure]:max-w-full [&_figure]:self-center [&_img]:max-h-[200px] [&_img]:max-w-full [&_img]:object-contain [&_img]:w-auto [&_img]:h-auto';

const CardHeightContext = React.createContext<{
    registerCard: (id: string, el: HTMLElement | null) => void;
    cardMinHeight: number;
}>({ registerCard: () => {}, cardMinHeight: 0 });

function useCardHeight(id: string) {
    const { registerCard, cardMinHeight } = React.useContext(CardHeightContext);
    const ref = React.useCallback(
        (el: HTMLElement | null) => registerCard(id, el),
        [id, registerCard],
    );
    return { ref, cardMinHeight };
}

const INITIAL_CARD_HEIGHT = 100;
const CALC_DELAY_MS = 350;
const HEADER_AND_GAP_RESERVE = 100;
const IFRAME_MIN_VIEWPORT = 420;
const ASPECT_FROM_WIDTH = 0.8;
const LARGE_VIEWPORT_THRESHOLD = 700;

const BREAKPOINTS = [640, 768, 1024, 1280] as const;

function getBreakpointKey(): string {
    if (typeof window === 'undefined') return 'lg';
    const w = window.innerWidth;
    if (w >= 1280) return 'xl';
    if (w >= 1024) return 'lg';
    if (w >= 768) return 'md';
    if (w >= 640) return 'sm';
    return 'xs';
}

function useBreakpoint(): string {
    const [breakpoint, setBreakpoint] = React.useState(getBreakpointKey);

    React.useEffect(() => {
        const mqls = BREAKPOINTS.map(
            (px) =>
                [px, window.matchMedia(`(min-width: ${px}px)`)] as const,
        );

        const sync = () => setBreakpoint(getBreakpointKey());

        mqls.forEach(([, mq]) => mq.addEventListener('change', sync));
        return () => mqls.forEach(([, mq]) => mq.removeEventListener('change', sync));
    }, []);

    return breakpoint;
}

function CardHeightProvider({
    children,
    itemCount,
    optionCount,
}: {
    children: React.ReactNode;
    itemCount: number;
    optionCount: number;
}) {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const cardsRef = React.useRef<Map<string, HTMLElement>>(new Map());
    const breakpoint = useBreakpoint();
    const [cardMinHeight, setCardMinHeight] = React.useState(INITIAL_CARD_HEIGHT);

    const registerCard = React.useCallback((id: string, el: HTMLElement | null) => {
        if (el) {
            cardsRef.current.set(id, el);
        } else {
            cardsRef.current.delete(id);
        }
    }, []);

    const runCalc = React.useCallback(() => {
        const container = containerRef.current;
        if (!container) return;

        const parent = container.parentElement;
        const parentH = parent?.clientHeight ?? 0;
        const parentW = parent?.clientWidth ?? 0;
        const containerW = container.clientWidth;
        const inIframe = typeof window !== 'undefined' && window.self !== window.top;
        const viewportH = inIframe
            ? Math.max(window.innerHeight, IFRAME_MIN_VIEWPORT)
            : window.innerHeight;
        const viewportW = window.innerWidth;

        const useViewportForHeight =
            viewportH >= LARGE_VIEWPORT_THRESHOLD;
        const availableH =
            useViewportForHeight || parentH <= 0
                ? viewportH
                : Math.min(parentH, viewportH);
        const availableW =
            parentW > 0 ? Math.min(parentW, viewportW) : containerW;

        const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
        const columnW = isDesktop ? availableW / 2 : availableW;
        const cardCount = Math.max(
            Math.max(itemCount, optionCount),
            1,
        );
        const heightFromWidth = columnW * ASPECT_FROM_WIDTH;
        const heightFromParent =
            (availableH - HEADER_AND_GAP_RESERVE) / cardCount;
        const heightFromViewport =
            (viewportH - HEADER_AND_GAP_RESERVE) / cardCount;
        const cap = Math.max(
            Math.min(
                heightFromWidth,
                heightFromParent,
                heightFromViewport,
            ),
            80,
        );

        let maxH = 0;
        cardsRef.current.forEach((el) => {
            // Cards use min-height (not a fixed height) so content is never clipped; reset it
            // while measuring or the previous value would act as a floor and never shrink.
            const prevH = el.style.height;
            const prevMin = el.style.minHeight;
            el.style.height = 'auto';
            el.style.minHeight = '0';
            maxH = Math.max(maxH, el.scrollHeight);
            el.style.height = prevH;
            el.style.minHeight = prevMin;
        });

        const clamped = Math.min(Math.max(maxH, 80), cap);
        setCardMinHeight(clamped);
    }, [itemCount, optionCount]);

    React.useEffect(() => {
        const timer = setTimeout(runCalc, CALC_DELAY_MS);
        return () => clearTimeout(timer);
    }, [breakpoint, itemCount, optionCount, runCalc]);

    const ctxValue = React.useMemo(
        () => ({ registerCard, cardMinHeight }),
        [registerCard, cardMinHeight],
    );

    return (
        <CardHeightContext.Provider value={ctxValue}>
            <div ref={containerRef}>
                {children}
            </div>
        </CardHeightContext.Provider>
    );
}

/**
 * Droppable wrapper for the options column -- accepts return drags.
 * Expands to fill the column so the drop zone matches the question column height.
 */
function OptionsReturnWrapper({
    children,
    isDragReturn,
}: {
    children: React.ReactNode;
    isDragReturn: boolean;
}) {
    const { setNodeRef, isOver } = useDroppable({ id: OPTIONS_RETURN_ID });

    return (
        <div
            ref={setNodeRef}
            className={cn(
                'flex-1 min-h-0 flex flex-col rounded-lg p-2 -m-2 transition-all duration-200',
                isDragReturn && !isOver && 'border-2 border-dashed border-primary/30 bg-primary/[0.02]',
                isDragReturn && isOver && 'border-2 border-dashed border-primary bg-primary/10 shadow-inner',
            )}
        >
            <div className="flex-1 min-h-0 space-y-3 sm:space-y-4">
                {children}
            </div>
        </div>
    );
}

function DroppableDestination({
    item,
    answer,
    options,
    itemState,
    isSelected,
    isDragActive,
    isRemoving,
    onSelect,
    onRemove,
    disabled,
    showCorrectAnswer,
    showFeedback,
    result,
    isReview,
    childLabel,
    onDestinationAnchorRef,
    highlightItem,
    t,
}: {
    item: MatchItem;
    answer: MatchAnswer;
    options: MatchOption[];
    itemState: DroppableState;
    isSelected: boolean;
    isDragActive: boolean;
    isRemoving: boolean;
    onSelect: () => void;
    onRemove: () => void;
    disabled: boolean;
    showCorrectAnswer: boolean;
    showFeedback: boolean;
    result?: GradingResult;
    isReview: boolean;
    childLabel?: number;
    onDestinationAnchorRef?: (element: HTMLDivElement | null) => void;
    highlightItem?: boolean;
    t: (key: string, fallback?: string) => string;
}) {
    const matchedOptionId = answer[item.id];
    const hasContent =
        getItemText(item).trim() !== '' ||
        (item.content.type === 'image' && 'src' in item.content && item.content.src) ||
        (item.content.type === 'audio' && 'src' in item.content && item.content.src);

    const showCorrectBadge =
        showCorrectAnswer &&
        ((showFeedback && result && itemState === 'incorrect') ||
            (isReview && !matchedOptionId));

    const correctOption = showCorrectBadge
        ? options.find((o) => o.id === item.correctOptionId)
        : undefined;

    const canDragBack = !!matchedOptionId && !disabled;
    const { attributes, listeners, setNodeRef: setDragRef, isDragging } = useDraggable({
        id: `${RETURN_PREFIX}${item.id}`,
        disabled: !canDragBack,
    });

    const { ref: measureRef, cardMinHeight } = useCardHeight(`item-${item.id}`);
    const setDestinationRef = React.useCallback(
        (el: HTMLDivElement | null) => {
            measureRef(el);
            onDestinationAnchorRef?.(el);
        },
        [measureRef, onDestinationAnchorRef]
    );

    return (
        <DroppableZone
            ref={setDestinationRef}
            id={`${DROPPABLE_PREFIX}${item.id}`}
            state={itemState}
            disabled={disabled}
            isSelected={isSelected}
            isDragActive={isDragActive}
            onClick={onSelect}
            className={cn(
                'flex flex-col items-stretch',
                isRemoving && 'animate-pulse',
                canDragBack && 'cursor-grab active:cursor-grabbing',
                isDragging && 'opacity-40 scale-[0.98] ring-2 ring-primary',
                highlightItem &&
                    'ring-2 ring-primary/50 ring-offset-2 ring-offset-background transition-[box-shadow] duration-300'
            )}
            style={cardMinHeight > 0 ? { minHeight: cardMinHeight } : undefined}
        >
            {canDragBack && (
                <div
                    ref={setDragRef}
                    {...attributes}
                    {...listeners}
                    className="absolute inset-0 z-20 touch-none"
                    title={t('dragToMoveAnswer')}
                    onClick={(e) => e.stopPropagation()}
                />
            )}
            {hasContent ? (
                <div
                    className={cn(
                        'min-w-0 min-h-0 flex-1 self-stretch flex w-full items-stretch pointer-events-none',
                        childLabel != null ? 'flex-row gap-2 pl-2' : ''
                    )}
                >
                    {childLabel != null ? (
                        <div className="flex shrink-0 items-center self-stretch py-2">
                            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted-foreground/15 text-sm font-bold text-foreground">
                                {childLabel}
                            </div>
                        </div>
                    ) : null}
                    <div className={MATCH_CARD_CONTENT_CLASS}>
                        <BlockRenderer content={item.content} />
                    </div>
                </div>
            ) : (
                <div className="flex-1 self-stretch flex items-center justify-center text-muted-foreground pointer-events-none">
                    <HelpCircle className="h-5 w-5" />
                </div>
            )}
            {/*
                Badge strip: its own row above the item content (order-first), right-aligned,
                instead of absolute top-right where it covered the item text on narrow screens.
            */}
            <div className="order-first relative z-30 mb-1 flex shrink-0 items-center justify-end gap-1.5 self-end pointer-events-none [&_button]:pointer-events-auto">
                {childLabel == null ? (
                    <div className="w-7 h-7 rounded bg-muted-foreground text-background flex items-center justify-center text-sm font-bold shrink-0">
                        #{item.rank}
                    </div>
                ) : null}
                {matchedOptionId ? (
                    <div
                        className={cn(
                            'flex items-center gap-1 animate-in fade-in slide-in-from-right-2 duration-200',
                            // WHY: same duplicate-ghost issue as DraggableOption
                            // below — while dragging the placed answer back out
                            // via the invisible drag-handle overlay above, this
                            // badge otherwise stays visible in place while
                            // MatchDragOverlay also renders a copy near the
                            // cursor. Hide it here instead of passing
                            // useDragOverlay (this isn't a DraggableItem).
                            isDragging && 'opacity-0'
                        )}
                    >
                        <div
                            className={cn(
                                'w-7 h-7 rounded flex items-center justify-center text-sm font-bold shrink-0',
                                itemState === 'correct' && 'bg-success text-success-foreground',
                                itemState === 'incorrect' && 'bg-destructive text-destructive-foreground',
                                itemState !== 'correct' && itemState !== 'incorrect' && 'bg-primary text-primary-foreground',
                            )}
                        >
                            {options.find((o) => o.id === matchedOptionId)?.letter ?? matchedOptionId.replace(/^opt-/, '')}
                        </div>
                        {!disabled && (
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon-xs"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onRemove();
                                }}
                                className={cn(
                                    'w-6 h-6 rounded shrink-0',
                                    'bg-muted-foreground/20 text-muted-foreground hover:bg-destructive hover:text-destructive-foreground',
                                )}
                                title={t('removeAnswer')}
                            >
                                <X className="size-3.5" />
                            </Button>
                        )}
                    </div>
                ) : (
                    <div className="w-7 h-7 rounded border border-dashed border-muted-foreground/40 flex items-center justify-center text-muted-foreground shrink-0">
                        <HelpCircle className="h-3.5 w-3.5" />
                    </div>
                )}
                {correctOption && (
                    <div className="w-7 h-7 rounded bg-success text-success-foreground flex items-center justify-center text-sm font-bold shrink-0">
                        {correctOption.letter}
                    </div>
                )}
            </div>
        </DroppableZone>
    );
}

function DraggableOption({
    option,
    optionState,
    onSelect,
    disabled,
}: {
    option: MatchOption;
    optionState: DraggableState;
    onSelect: () => void;
    disabled: boolean;
}) {
    const { ref: measureRef, cardMinHeight } = useCardHeight(`option-${option.id}`);

    return (
        <DraggableItem
            ref={measureRef}
            id={`${DRAGGABLE_PREFIX}${option.id}`}
            state={optionState}
            disabled={disabled}
            onClick={onSelect}
            // WHY: without this, the source card stays visible and follows
            // the cursor via its own drag transform while MatchDragOverlay
            // also renders a second copy near the mouse — two visible
            // ghosts of the same card. Hiding the source (opacity 0) leaves
            // only the DragOverlay clone, matching FITBPlayerDrag's chips.
            useDragOverlay
            className="flex items-start gap-2"
            style={cardMinHeight > 0 ? { minHeight: cardMinHeight } : undefined}
        >
            {/* Letter badge sits in the flex row (not absolute) so it can never overlap the answer text */}
            <Badge
                variant="secondary"
                className={cn(
                    'shrink-0 w-7 justify-center',
                    optionState === 'correct' && 'bg-success text-success-foreground',
                    optionState === 'incorrect' && 'bg-destructive text-destructive-foreground',
                    optionState !== 'correct' && optionState !== 'incorrect' && 'bg-success/10 text-success',
                )}
            >
                {option.letter}
            </Badge>
            <div className={MATCH_CARD_CONTENT_CLASS}>
                <BlockRenderer
                    content={getDisplayBlockContent(option.content, getOptionText(option))}
                />
            </div>
        </DraggableItem>
    );
}

function MatchDragOverlay({ activeOption }: { activeOption?: MatchOption }) {
    const { cardMinHeight } = React.useContext(CardHeightContext);

    return (
        <DragOverlay dropAnimation={{ duration: 200, easing: 'ease' }}>
            {activeOption ? (
                <div
                    className="rounded-sm border border-primary/60 bg-background shadow-xl p-2 flex items-start gap-2 opacity-90 pointer-events-none relative"
                    style={cardMinHeight > 0 ? { minHeight: cardMinHeight } : undefined}
                >
                    <Badge
                        variant="secondary"
                        className="shrink-0 w-7 justify-center bg-success/10 text-success"
                    >
                        {activeOption.letter}
                    </Badge>
                    <div className={MATCH_CARD_CONTENT_CLASS}>
                        <BlockRenderer
                            content={getDisplayBlockContent(activeOption.content, getOptionText(activeOption))}
                        />
                    </div>
                </div>
            ) : null}
        </DragOverlay>
    );
}

// Stable default (an inline `= {}` is a new object each render; keeps memo/effect deps stable).
const EMPTY_ANSWER: Record<string, string> = {};

export const MatchPlayer: React.FC<MatchPlayerProps> = ({
    data,
    answer = EMPTY_ANSWER,
    onAnswer,
    disabled = false,
    showFeedback = false,
    result,
    isReview = false,
    className,
    config,
    elementChildLabels,
    highlightMatchItemId = null,
    onMatchItemAnchor,
    schema: schemaProp,
    language,
}) => {
    const schema = schemaProp ?? matchElementSchema;
    const { t } = useElementSchemaLocale(schema.i18n, { config, language });
    const pointerSensor = useSensor(PointerSensor, {
        activationConstraint: { distance: 5 },
    });
    const touchSensor = useSensor(TouchSensor, {
        activationConstraint: { delay: 150, tolerance: 5 },
    });
    const sensors = useSensors(pointerSensor, touchSensor);

    const matchData = React.useMemo(() => normalizeMatchData(data || {}), [data]);

    const [selectedItemId, setSelectedItemId] = React.useState<string | null>(null);
    const [displayOptions, setDisplayOptions] = React.useState<MatchOption[]>([]);
    const [isDragActive, setIsDragActive] = React.useState(false);
    const [isDragReturn, setIsDragReturn] = React.useState(false);
    const [activeDragId, setActiveDragId] = React.useState<string | null>(null);
    const [removingItemId, setRemovingItemId] = React.useState<string | null>(null);

    const { items, options, shuffleOptions, oneToOne } = matchData;

    const showCorrectAnswer =
        (config?.behavior as BehaviorConfig | undefined)?.showCorrectAnswer ?? true;

    const isLocked = disabled || isReview || (showFeedback && !!result);

    React.useEffect(() => {
        setSelectedItemId(null);
    }, [showFeedback]);

    const optionIdsKey = options.map((o) => o.id).join(',');
    const shuffledOnceRef = React.useRef(false);

    React.useEffect(() => {
        if (shuffleOptions && (!isReview || !showCorrectAnswer)) {
            if (!shuffledOnceRef.current) {
                setDisplayOptions(shuffleArray([...options]));
                shuffledOnceRef.current = true;
            } else {
                setDisplayOptions((prev) => {
                    const idOrder = prev.map((o) => o.id);
                    const optMap = new Map(options.map((o) => [o.id, o]));
                    const reordered = idOrder
                        .map((id) => optMap.get(id))
                        .filter((o): o is MatchOption => !!o);
                    const newOpts = options.filter((o) => !idOrder.includes(o.id));
                    return [...reordered, ...newOpts];
                });
            }
        } else {
            setDisplayOptions(options);
            shuffledOnceRef.current = false;
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [optionIdsKey, shuffleOptions, isReview, showCorrectAnswer]);

    const usedOptionIds = React.useMemo(() => {
        return new Set(Object.values(answer));
    }, [answer]);

    const getItemState = (item: MatchItem): DroppableState => {
        // ELMS-2370: hide answer => keep learner pairing, no right/wrong colouring.
        if (!showCorrectAnswer) {
            return answer[item.id] ? 'active' : 'default';
        }
        if (isReview) {
            const userOption = answer[item.id];
            if (!userOption) return 'default';
            return userOption === item.correctOptionId ? 'correct' : 'incorrect';
        }
        if (!showFeedback || !result) {
            return answer[item.id] ? 'active' : 'default';
        }
        const itemResults = result.details?.itemResults as Record<string, boolean> | undefined;
        if (itemResults) {
            if (itemResults[item.id] === true) return 'correct';
            if (itemResults[item.id] === false) return 'incorrect';
        } else {
            const userOption = answer[item.id];
            if (!userOption) return 'default';
            return userOption === item.correctOptionId ? 'correct' : 'incorrect';
        }
        return 'default';
    };

    const getOptionState = (option: MatchOption): DraggableState => {
        if (!showCorrectAnswer || !showFeedback || !result) {
            if (oneToOne && usedOptionIds.has(option.id)) return 'used';
            return selectedItemId && answer[selectedItemId] === option.id ? 'selected' : 'default';
        }
        const itemUsingOption = matchData.items.find((i) => answer[i.id] === option.id);
        if (!itemUsingOption) return 'default';
        const itemResults = result.details?.itemResults as Record<string, boolean> | undefined;
        if (itemResults) {
            return itemResults[itemUsingOption.id] ? 'correct' : 'incorrect';
        }
        return answer[itemUsingOption.id] === itemUsingOption.correctOptionId ? 'correct' : 'incorrect';
    };

    const handleDragStart = (event: DragStartEvent) => {
        const id = String(event.active.id);
        const isReturn = id.startsWith(RETURN_PREFIX);
        setIsDragReturn(isReturn);

        if (isReturn) {
            const itemId = id.slice(RETURN_PREFIX.length);
            const optionId = answer[itemId];
            setActiveDragId(optionId ?? null);
        } else {
            setActiveDragId(id.startsWith(DRAGGABLE_PREFIX) ? id.slice(DRAGGABLE_PREFIX.length) : id);
        }

        setIsDragActive(true);
        setSelectedItemId(null);
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const wasReturn = isDragReturn;

        const cleanup = () => {
            setActiveDragId(null);
            setIsDragActive(false);
            setIsDragReturn(false);
        };

        const { active, over } = event;
        if (!over || isLocked) {
            cleanup();
            return;
        }

        const activeId = String(active.id);
        const overId = String(over.id);

        if (wasReturn && activeId.startsWith(RETURN_PREFIX)) {
            const itemId = activeId.slice(RETURN_PREFIX.length);
            if (overId === OPTIONS_RETURN_ID || overId.startsWith(DRAGGABLE_PREFIX)) {
                const { [itemId]: _, ...rest } = answer;
                onAnswer?.(rest);
            } else if (overId.startsWith(DROPPABLE_PREFIX)) {
                const targetItemId = overId.slice(DROPPABLE_PREFIX.length);
                if (targetItemId !== itemId) {
                    const optionId = answer[itemId];
                    if (optionId) {
                        const { [itemId]: _, ...rest } = answer;
                        onAnswer?.({ ...rest, [targetItemId]: optionId });
                    }
                }
            }
            // Defer cleanup so the DragOverlay drop animation can finish
            requestAnimationFrame(cleanup);
            return;
        }

        if (!activeId.startsWith(DRAGGABLE_PREFIX) || !overId.startsWith(DROPPABLE_PREFIX)) {
            cleanup();
            return;
        }

        const optionId = activeId.slice(DRAGGABLE_PREFIX.length);
        const itemId = overId.slice(DROPPABLE_PREFIX.length);

        if (oneToOne && usedOptionIds.has(optionId)) {
            cleanup();
            return;
        }

        const newAnswer = { ...answer, [itemId]: optionId };
        onAnswer?.(newAnswer);
        // Defer cleanup so the DragOverlay drop animation can finish
        requestAnimationFrame(cleanup);
    };

    const handleDragCancel = () => {
        setActiveDragId(null);
        setIsDragActive(false);
        setIsDragReturn(false);
    };

    const activeOption = activeDragId
        ? displayOptions.find((o) => o.id === activeDragId)
        : undefined;

    const handleOptionClick = (optionId: string) => {
        if (isLocked) return;
        if (oneToOne && usedOptionIds.has(optionId)) return;

        if (selectedItemId) {
            const newAnswer = { ...answer, [selectedItemId]: optionId };
            onAnswer?.(newAnswer);
            setSelectedItemId(null);
        }
    };

    const handleDestinationClick = (itemId: string) => {
        if (isLocked || isDragActive) return;
        setSelectedItemId((prev) => (prev === itemId ? null : itemId));
    };

    const handleRemoveAnswer = (itemId: string) => {
        if (isLocked) return;
        setRemovingItemId(itemId);
        setTimeout(() => {
            const { [itemId]: _, ...rest } = answer;
            onAnswer?.(rest);
            setRemovingItemId(null);
        }, 150);
    };

    if (!data || items.length === 0 || options.length === 0) {
        return (
            <div className={cn('text-muted-foreground italic p-4', className)}>
                {t('matchPlayerNoData')}
            </div>
        );
    }

    return (
        <CardHeightProvider
            itemCount={items.length}
            optionCount={displayOptions.length}
        >
            <div className={cn('match-player space-y-3 sm:space-y-4', className)}>
                <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragCancel={handleDragCancel}>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
                        <div className="flex flex-col gap-3 sm:gap-4">
                            <h3 className="text-base sm:text-lg font-semibold">
                                {t('matchPlayerInstruction')}
                            </h3>
                            <div className="space-y-3 sm:space-y-4">
                                {items.map((item) => (
                                    <DroppableDestination
                                        key={item.id}
                                        item={item}
                                        answer={answer}
                                        options={matchData.options}
                                        itemState={getItemState(item)}
                                        isSelected={selectedItemId === item.id}
                                        isDragActive={isDragActive}
                                        isRemoving={removingItemId === item.id}
                                        onSelect={() => handleDestinationClick(item.id)}
                                        onRemove={() => handleRemoveAnswer(item.id)}
                                        disabled={isLocked}
                                        showCorrectAnswer={showCorrectAnswer}
                                        showFeedback={showFeedback}
                                        result={result}
                                        isReview={isReview}
                                        childLabel={elementChildLabels?.matchItem?.[item.id]}
                                        onDestinationAnchorRef={(el) =>
                                            onMatchItemAnchor?.(item.id, el)
                                        }
                                        highlightItem={highlightMatchItemId === item.id}
                                        t={t}
                                    />
                                ))}
                            </div>
                        </div>

                        <div className="flex flex-col gap-3 sm:gap-4 min-h-0 lg:border-l lg:border-border lg:pl-8">
                            <h3 className="text-base sm:text-lg font-semibold shrink-0">
                                {t('matchingOptionsHeading')}
                            </h3>
                            <OptionsReturnWrapper isDragReturn={isDragReturn}>
                                {displayOptions.map((option) => (
                                    <DraggableOption
                                        key={option.id}
                                        option={option}
                                        optionState={getOptionState(option)}
                                        onSelect={() => handleOptionClick(option.id)}
                                        disabled={isLocked}
                                    />
                                ))}
                            </OptionsReturnWrapper>
                        </div>
                    </div>

                    <MatchDragOverlay activeOption={activeOption} />
                </DndContext>

                {/* Explanation (after grading) */}
                {showFeedback && result && showCorrectAnswer && matchData.explanation && (
                    <div className="match-player-explanation mt-4 p-4 rounded-lg bg-info/10">
                        <h4 className="match-player-explanation-title font-medium mb-1">{t('explanation')}</h4>
                        <p className="match-player-explanation-body text-sm text-muted-foreground">
                            {matchData.explanation}
                        </p>
                    </div>
                )}
            </div>
        </CardHeightProvider>
    );
};

export default MatchPlayer;
